import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useState,
} from "react"
import { get, post } from "../services/api/api"
import Quiz from "../models/quiz"
import { MissionsContext } from "./missions"
import { MainContext } from "./main"
import { parseQuizzesData } from "../services/utils/parseFunctions"
import { MissionSubType } from "../services/config/enum"
import { logger } from "../services/utils/utils"

interface QuizzesContextInterface {
  quizzesLoading: boolean
  submitLoading: boolean
  setSubmitLoading: Dispatch<SetStateAction<boolean>>
  quizzesError: boolean
  currentQuiz: Quiz | null
  getQuizzesAndSetCurrentQuiz: () => Promise<void>
  submitQuiz: (
    isCorrect: boolean,
    missionId: string,
    quizId: string,
    answer: string
  ) => Promise<void>
}

const QuizzesContext = createContext<QuizzesContextInterface>({
  quizzesLoading: true,
  submitLoading: false,
  setSubmitLoading: () => {},
  quizzesError: false,
  currentQuiz: null,
  getQuizzesAndSetCurrentQuiz: async () => {},
  submitQuiz: async () => {},
})

const QuizzesController = ({ children }: { children: ReactNode }) => {
  const { lang, currentMission } = useContext(MainContext)
  const { missions, updateAll } = useContext(MissionsContext)

  // loadings
  const [quizzesLoading, setQuizzesLoading] = useState<boolean>(true)
  const [submitLoading, setSubmitLoading] = useState<boolean>(false)

  // errors
  const [quizzesError, setQuizzesError] = useState<boolean>(false)

  // states
  const [currentQuiz, setCurrentQuiz] = useState<Quiz | null>(null)

  // get quizzes
  const getQuizzesAndSetCurrentQuiz = async () => {
    setQuizzesLoading(true)
    setQuizzesError(false)

    try {
      const dailyQuizMissions = missions.filter(
        (mission) =>
          mission.missionSubType === MissionSubType.DAILYQUIZ &&
          !mission.satisfied
      )

      const { data } = await get(
        `/web/learn/quiz?numQuiz=${dailyQuizMissions.length}`
      )

      const parsedData = parseQuizzesData(data.items.items, lang)
      logger("quizzes", parsedData)

      // set current quiz
      // each quiz in the array maps to its respective DAILYQUIZ mission in order
      let currentQuizToSet: Quiz | null = null
      const currentMissionIndex = dailyQuizMissions.findIndex(
        (mission) => mission.missionId === currentMission?.missionId
      )
      if (currentMissionIndex >= 0 && parsedData[currentMissionIndex]) {
        currentQuizToSet = parsedData[currentMissionIndex]
      }

      logger("current quiz", currentQuizToSet)

      setCurrentQuiz(currentQuizToSet)
      setQuizzesLoading(false)
    } catch (e: any) {
      logger("quizzes error", e)
      setQuizzesLoading(false)
      setQuizzesError(true)
    }
  }

  // submit quiz
  const submitQuiz = async (
    isCorrect: boolean,
    missionId: string,
    quizId: string,
    answer: string
  ) => {
    setSubmitLoading(true)

    try {
      await post("/web/learn/quiz/submit", {
        isCorrect,
        missionId,
        quizId,
        answer,
      })

      // update
      updateAll()
    } catch (e: any) {
      logger("quiz submit error", e)
      setQuizzesError(true)
    }
  }

  return (
    <QuizzesContext.Provider
      value={{
        quizzesLoading,
        submitLoading,
        setSubmitLoading,
        quizzesError,
        currentQuiz,
        getQuizzesAndSetCurrentQuiz,
        submitQuiz,
      }}
    >
      {children}
    </QuizzesContext.Provider>
  )
}
export { QuizzesController, QuizzesContext }
